import * as React from "react"
import * as CRC from "models/crc";
import { AppContext } from "AppContext";
import { ActionTypes } from "reducer/actions";
import { Button, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from "@mui/material";
import ReportApi from "api/reportApi";
import * as fileHelper from "helpers/fileHelper";
import { FilterContext } from "models/core/filter";
import { ColonIndicatorDefinitionsUrl, RectalIndicatorDefinitionsUrl } from "config/appUrlConfig";
import { DashChip } from "components/common/DashChips";
import { Download, Downloading } from "@mui/icons-material";
import { getStateIndicatorReferences } from "api/dashboardApi";
import Loader from "pages/shared/pageloader";

enum CRCIndicatorAction {
	UserClickedDownloadIndicator,
	DownloadIndicatorSuccess,
	DownloadIndicatorFail
}

type Action =
	{ type: CRCIndicatorAction.UserClickedDownloadIndicator, payload: number }
	| { type: CRCIndicatorAction.DownloadIndicatorSuccess }
	| { type: CRCIndicatorAction.DownloadIndicatorFail }


interface State {
	indicator: number
}

const initialState: State = {
	indicator: 0
}



const reducer = (state: State, action: Action): State => {
	const { type } = action;

	switch (type) {
		case CRCIndicatorAction.UserClickedDownloadIndicator:
			return { ...state, ...{ indicator: action.payload } };

		case CRCIndicatorAction.DownloadIndicatorSuccess:
		case CRCIndicatorAction.DownloadIndicatorFail:
			return { ...state, ...{ indicator: 0 } };

		default:
			return state;
	}
}

let stateWideData: {
	title?: string;
	patientsColon?: string;
	daysFromDiagToSurgLT30Colon?: string;
	hospitalStayDaysColon?: string;
	prolongedLOSGT21DaysColon?: string;
	hadAnastomicLeakColon?: string;
	hadAnastomicLeakAndDiedWithin90DaysColon?: string;
	lapConvertedToOpenSurgColon?: string;
	hadStomaAtSurgColon?: string;
	inHospitalMortalityColon?: string;
	mortality30DaysColon?: string;
	mortality90DaysColon?: string;
	stage3AdjuvantSTColon?: string;
	oneYearSurvivalColon?: string;
	twoYearSurvivalColon?: string;
	fiveYearSurvivalColon?: string;
	patientsRectal?: string;
	daysFromDiagToSurgLT30Rectal?: string;
	hospitalStayDaysRectal?: string;
	prolongedLOSGT21DaysRectal?: string;
	hadAnastomicLeakRectal?: string;
	hadAnastomicLeakAndDiedWithin90DaysRectal?: string;
	lapConvertedToOpenSurgRectal?: string;
	hadStomaAtSurgRectal?: string;
	hadAbdominoperinealResectionRectal?: string;
	inHospitalMortalityRectal?: string;
	mortality30DaysRectal?: string;
	mortality90DaysRectal?: string;
	stage3AdjuvantSTRectal?: string;
	neoadjuvantXRTRectal?: string;
	oneYearSurvivalRectal?: string;
	twoYearSurvivalRectal?: string;
	fiveYearSurvivalRectal?: string;
	hadFirstIVSTWT42DaysColon?: string;
	hadFirstIVSTWT42DaysRectal?: string;
	hadFirstTreatmentWT30DaysColon?: string;
	hadFirstTreatmentWT30DaysRectal?: string;
} = {};

export function CrcDashboard() {
	const { state, dispatch } = React.useContext(AppContext);
	const [localState, localDispatch] = React.useReducer(reducer, initialState)

	const [showFilterChips, setFilterChips] = React.useState(true);

	//Set states to be able to switch between indicator views 
	const [viewBy, setViewBy] = React.useState(null);
	const [daysFromDiagToSurgInd, setDaysFromDiagToSurgInd] = React.useState(0);
	const [inHospitalMortalityInd, setInHospitalMortalityInd] = React.useState(0);
	const [thirtyDayMortalityInd, setThirtyDayMortalityInd] = React.useState(0);
	const [ninetyDayMortalityInd, setNinetyDayMortalityInd] = React.useState(0);
	const [oneYearSurvivalInd, setOneYearSurvivalInd] = React.useState(0);
	const [twoYearSurvivalInd, setTwoYearSurvivalInd] = React.useState(0);
	const [stage3AdjuvantTxInd, setStage3AdjuvantTxInd] = React.useState(0);
	const [neoAdjuvantXRTInd, setNeoAdjuvantXRTInd] = React.useState(0);

	const addFilter: CRC.AddFilterFunc = async (key: keyof typeof CRC.DashboardFilterKeys, value: string | string[], context?: FilterContext, labelOverride?: string) => {
		const filteredDashboard = await CRC.computeChartData(CRC.addFilter(state.page.dashboard as CRC.IMDTDashboard)(key, value, context, labelOverride)); // state = what the other filter function returns 
		dispatch({ type: ActionTypes.UserClickedOnChart, payload: filteredDashboard });
	}

	const removeFilter: CRC.RemoveFilterFunc = async (key: keyof typeof CRC.DashboardFilterKeys, value?: string) => {

		const filteredDashboard = await CRC.computeChartData(CRC.removeFilter(state.page.dashboard as CRC.IMDTDashboard, key, value));
		dispatch({ type: ActionTypes.UserClickedOnChart, payload: filteredDashboard });
	}

	React.useEffect(() => {
		dispatch({ type: ActionTypes.UserWantsToLoadMdtDashboard });
		const fetchStateReferenceData = async () => {
			stateWideData = await getStateIndicatorReferences("colorectal");
		}

		const loadDashboard = async () => {
			const value = await CRC.loadMdtDashboard(state.page.dashboard as CRC.IMDTDashboard);
			dispatch({ type: ActionTypes.UserLoadedMdtDashboard, payload: value });
		}

		Promise.all([fetchStateReferenceData(), loadDashboard()]);
		return () => {
			CRC.closeMdtDashboard();
		}
	}, []);

	React.useEffect(() => {
		if (localState.indicator == 0)
			return;

		let ids = crcData.level1.filteredReferralIdCsv;
		let alldata = false;


		const getCRCIndicatorPatientList = async () => {
			try {
				const data = await ReportApi.getCRCIndicatorPatientList(ids, alldata, localState.indicator);
				if (data.errorMessage) {
					console.error(data.errorMessage);
					localDispatch({ type: CRCIndicatorAction.DownloadIndicatorFail })
				}
				localDispatch({ type: CRCIndicatorAction.DownloadIndicatorSuccess })
				return fileHelper.showFile(data.bytes, data.filename, fileHelper.fileTypeToMimeType(data.filetype), "exportPatientsElem")
			} catch {
				localDispatch({ type: CRCIndicatorAction.DownloadIndicatorFail })
			}
		}

		getCRCIndicatorPatientList();


	}, [localState.indicator]);

	if (state.page.loading || !state.page.dashboard.chartData)
		return <Loader />

	//Create function to show/hide filter chips
	const toggleChips = () => {
		if (showFilterChips) {
			setFilterChips(false);
		} else {
			setFilterChips(true);
		}
	};

	let activeFiltersArray = [];
	let filterArray = [];
	let chips = [];

	for (const key in state.page.dashboard.filters) {
		if (Object.prototype.hasOwnProperty.call(state.page.dashboard.filters, key)) {
			activeFiltersArray.push({ "key": key, "values": state.page.dashboard.filters[key].value, "parts": state.page.dashboard.filters[key].parts });
		}
	}

	for (const key in CRC.DashboardFilterKeys) {
		if (Object.prototype.hasOwnProperty.call(CRC.DashboardFilterKeys, key)) {
			filterArray.push({ "key": key, "label": CRC.DashboardFilterKeys[key].label });
		}
	}

	activeFiltersArray.forEach(a => {
		a.values.forEach(b => {
			chips.push(<DashChip key={a.key + b}
				removeFilterFn={removeFilter}
				filterName={filterArray.find(id => id.key == a.key).label}
				filterValue={b}
				filterId={a.key}
				filterParent={(a.parts.length > 1 ? a.parts[0] : null)}
			/>
			)
		})
	})

	const crcData = state.page.dashboard.chartData as CRC.IChartData;


	const downloadBtn = (extractTypeId: number) => {
		const { indicator } = localState;

		if (indicator == extractTypeId)
			return <Downloading />

		return (
			<Download style={{ cursor: "pointer" }} onClick={() =>
				localDispatch({ type: CRCIndicatorAction.UserClickedDownloadIndicator, payload: extractTypeId })} />
		)
	}


	const ecog = {
		colon: {
			numerator: 0,
			denominator: 0,
			percentage: 0
		},
		rectal: {
			numerator: 0,
			denominator: 0,
			percentage: 0
		}

	}

	const stage = {
		colon: {
			numerator: 0,
			denominator: 0,
			percentage: 0
		},
		rectal: {
			numerator: 0,
			denominator: 0,
			percentage: 0
		}
	}


	if (crcData.level1.totalDiagnoses.colon > 0) {

		ecog.colon.numerator = crcData.level1.totalDiagnoses.colon - (crcData.level2.ecogGroup.colon.values[crcData.level2.ecogGroup.colon.labels.indexOf("Unk")] || 0);
		ecog.colon.denominator = crcData.level1.totalDiagnoses.colon
		ecog.colon.percentage = ecog.colon.numerator / ecog.colon.denominator * 100

		stage.colon.numerator = crcData.level1.totalDiagnoses.colon - (crcData.level2.tnmStage.colon.values[crcData.level2.tnmStage.colon.labels.indexOf("Unk")] || 0);
		stage.colon.denominator = crcData.level1.totalDiagnoses.colon;
		stage.colon.percentage = stage.colon.numerator / stage.colon.denominator * 100
	}

	if (crcData.level1.totalDiagnoses.rectal > 0) {

		ecog.rectal.numerator = crcData.level1.totalDiagnoses.rectal - (crcData.level2.ecogGroup.rectal.values[crcData.level2.ecogGroup.rectal.labels.indexOf("Unk")] || 0);
		ecog.rectal.denominator = crcData.level1.totalDiagnoses.rectal
		ecog.rectal.percentage = ecog.rectal.numerator / ecog.rectal.denominator * 100

		stage.rectal.numerator = crcData.level1.totalDiagnoses.rectal - (crcData.level2.tnmStage.rectal.values[crcData.level2.tnmStage.rectal.labels.indexOf("Unk")] || 0);
		stage.rectal.denominator = crcData.level1.totalDiagnoses.rectal;
		stage.rectal.percentage = stage.rectal.numerator / stage.rectal.denominator * 100
	}

	const dashboard = <>
		<div style={{
			position: "sticky",
			top: "64px",
			left: "10px",
			zIndex: 2,
			backgroundColor: "white"
		}}>


			{(showFilterChips ? <div style={{ marginLeft: "15px", marginBottom: "15px", marginTop: "10px", width: "80%" }}>
				{chips}
			</div> : null)}
		</div>

		<div style={{
			maxWidth: 1000,
			margin: "0 auto"
		}}>

			<div style={{
				textAlign: "right",
				paddingBottom: "10px",
				paddingTop: "10px",
				position: "sticky",
				zIndex: 1,
				top: "60px",
				backgroundColor: "white"
			}}>
				<Button onClick={() => localDispatch({ type: CRCIndicatorAction.UserClickedDownloadIndicator, payload: 1 })}
					style={{
						backgroundColor: ((crcData.level1.totalDiagnoses.colon > 0 || crcData.level1.totalDiagnoses.rectal > 0) ? ("#1e88e5") : ("#d3d3d3")),
						color: "white",
						alignItems: "right"
					}}>
					Download Patient Details
				</Button>
			</div>

			<TableContainer component={Paper}>
				<Table size="small">
					<TableHead>
						<TableRow>
							<TableCell>Indicator</TableCell>
							<TableCell colSpan={2} align="center">My MDT(s)*</TableCell>
							<TableCell colSpan={2} align="center">{stateWideData.title}^</TableCell>
						</TableRow>

						<TableRow>
							<TableCell> </TableCell>
							<TableCell align="center">Colon</TableCell>
							<TableCell align="center">Rectal</TableCell>
							<TableCell align="center">Colon</TableCell>
							<TableCell align="center">Rectal</TableCell>
						</TableRow>

					</TableHead>
					<TableBody>
						<TableRow>
							<TableCell>Colorectal cancer diagnoses^</TableCell>
							<TableCell align="center">{crcData.level1.totalDiagnoses.colon}</TableCell>
							<TableCell align="center">{crcData.level1.totalDiagnoses.rectal}</TableCell>
							<TableCell align="center">{stateWideData.patientsColon}</TableCell>
							<TableCell align="center">{stateWideData.patientsRectal}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Percentage of patients with ECOG</TableCell>
							<TableCell align="center">{`${ecog.colon.percentage.toFixed(0)}%`} <br /> {`(${ecog.colon.numerator}/${ecog.colon.denominator})`}</TableCell>
							<TableCell align="center">{`${ecog.rectal.percentage.toFixed(0)}%`} <br /> {`(${ecog.rectal.numerator}/${ecog.rectal.denominator})`}</TableCell>
							<TableCell align="center">-</TableCell>
							<TableCell align="center">-</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Percentage of patients with Stage</TableCell>
							<TableCell align="center">{`${stage.colon.percentage.toFixed(0)}%`} <br /> {`(${stage.colon.numerator}/${stage.colon.denominator})`}</TableCell>
							<TableCell align="center">{`${stage.rectal.percentage.toFixed(0)}%`} <br /> {`(${stage.rectal.numerator}/${stage.rectal.denominator})`}</TableCell>
							<TableCell align="center">-</TableCell>
							<TableCell align="center">-</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Received first treatment within 30 days</TableCell>
							<TableCell align="center">{`${crcData.level1.diagToFirstTreatment30Days.colon.percentage.toFixed(0)}%`} <br /> {`(${crcData.level1.diagToFirstTreatment30Days.colon.numerator}/${crcData.level1.diagToFirstTreatment30Days.colon.denominator})`}</TableCell>
							<TableCell align="center">{`${crcData.level1.diagToFirstTreatment30Days.rectal.percentage.toFixed(0)}%`} <br /> {`(${crcData.level1.diagToFirstTreatment30Days.rectal.numerator}/${crcData.level1.diagToFirstTreatment30Days.rectal.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.hadFirstTreatmentWT30DaysColon}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.hadFirstTreatmentWT30DaysRectal}</TableCell>
						</TableRow>


						<TableRow>
							<TableCell colSpan={6}><b>Surgical</b></TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Received definitive resection as first treatment within 30 days of diagnosis</TableCell>
							<TableCell align="center">{`${crcData.level1.diagToSurg30Days.colon.percentage.toFixed(0)}%`} <br /> {`(${crcData.level1.diagToSurg30Days.colon.numerator}/${crcData.level1.diagToSurg30Days.colon.denominator})`}</TableCell>
							<TableCell align="center">{`${crcData.level1.diagToSurg30Days.rectal.percentage.toFixed(0)}%`} <br /> {`(${crcData.level1.diagToSurg30Days.rectal.numerator}/${crcData.level1.diagToSurg30Days.rectal.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.daysFromDiagToSurgLT30Colon}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.daysFromDiagToSurgLT30Rectal}</TableCell>
						</TableRow>

						{/* <TableRow>
						<TableCell>Hospital stay (Median days) </TableCell>
						<TableCell align = "center">{`${crcData.level1.lengthOfStay.colon.median}`} <br/> {`(${crcData.level1.lengthOfStay.colon.lowerquart} - ${crcData.level1.lengthOfStay.colon.upperquart})`}</TableCell>						
						<TableCell align = "center">{`${crcData.level1.lengthOfStay.rectal.median}`} <br/> {`(${crcData.level1.lengthOfStay.rectal.lowerquart} - ${crcData.level1.lengthOfStay.rectal.upperquart})`}</TableCell>
						<TableCell align = "center" style={{whiteSpace: "pre"}}>{stateWideData.hospitalStayDaysColon}</TableCell>
						<TableCell align = "center" style={{whiteSpace: "pre"}}>{stateWideData.hospitalStayDaysRectal}</TableCell>
					</TableRow> */}

						<TableRow>
							<TableCell> Prolonged LOS ≥ 21 days</TableCell>
							<TableCell align="center">{`${crcData.level1.prolongedLOS.colon.percentage.toFixed(1)}%`} <br /> {`(${crcData.level1.prolongedLOS.colon.numerator}/${crcData.level1.prolongedLOS.colon.denominator})`}</TableCell>
							<TableCell align="center">{`${crcData.level1.prolongedLOS.rectal.percentage.toFixed(1)}%`} <br /> {`(${crcData.level1.prolongedLOS.rectal.numerator}/${crcData.level1.prolongedLOS.rectal.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.prolongedLOSGT21DaysColon}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.prolongedLOSGT21DaysRectal}</TableCell>
						</TableRow>

						{/* <TableRow>
						<TableCell>Had an anastomotic leak</TableCell>
						<TableCell align = "center">{`${crcData.level1.hadAnastomicLeak.colon.percentage.toFixed(2)}%`} <br/> {`(${crcData.level1.hadAnastomicLeak.colon.numerator}/${crcData.level1.hadAnastomicLeak.colon.denominator})`}</TableCell>						
						<TableCell align = "center">{`${crcData.level1.hadAnastomicLeak.rectal.percentage.toFixed(2)}%`} <br/> {`(${crcData.level1.hadAnastomicLeak.rectal.numerator}/${crcData.level1.hadAnastomicLeak.rectal.denominator})`}</TableCell>
						<TableCell align = "center" style={{whiteSpace: "pre"}}>{stateWideData.hadAnastomicLeakColon}</TableCell>
						<TableCell align = "center" style={{whiteSpace: "pre"}}>{stateWideData.hadAnastomicLeakRectal}</TableCell>
						<TableCell align = "center">{(crcData.level1.hadAnastomicLeak.colon.denominator > 0 || crcData.level1.hadAnastomicLeak.rectal.denominator > 0) && downloadBtn(5)}</TableCell>
					</TableRow>
					<TableRow>
						<TableCell> Had an anastomotic leak and died within 90 days of definitive resection</TableCell>
						<TableCell align = "center">{`${crcData.level1.hadAnastomicLeakDied90Days.colon.percentage.toFixed(2)}%`} <br/> {`(${crcData.level1.hadAnastomicLeakDied90Days.colon.numerator}/${crcData.level1.hadAnastomicLeakDied90Days.colon.denominator})`}</TableCell>						
						<TableCell align = "center">{`${crcData.level1.hadAnastomicLeakDied90Days.rectal.percentage.toFixed(2)}%`} <br/> {`(${crcData.level1.hadAnastomicLeakDied90Days.rectal.numerator}/${crcData.level1.hadAnastomicLeakDied90Days.rectal.denominator})`}</TableCell>
						<TableCell align = "center" style={{whiteSpace: "pre"}}>{stateWideData.hadAnastomicLeakAndDiedWithin90DaysColon}</TableCell>
						<TableCell align = "center" style={{whiteSpace: "pre"}}>{stateWideData.hadAnastomicLeakAndDiedWithin90DaysRectal}</TableCell>
						<TableCell align = "center">{(crcData.level1.hadAnastomicLeakDied90Days.colon.denominator > 0 || crcData.level1.hadAnastomicLeakDied90Days.rectal.denominator > 0) && downloadBtn(6)}</TableCell>
					</TableRow> */}

						<TableRow>
							<TableCell>Stoma rate at definitive resection </TableCell>
							<TableCell align="center">{`${crcData.level1.hadStomaAtSurg.colon.percentage.toFixed(1)}%`} <br /> {`(${crcData.level1.hadStomaAtSurg.colon.numerator}/${crcData.level1.hadStomaAtSurg.colon.denominator})`}</TableCell>
							<TableCell align="center">{`${crcData.level1.hadStomaAtSurg.rectal.percentage.toFixed(0)}%`} <br /> {`(${crcData.level1.hadStomaAtSurg.rectal.numerator}/${crcData.level1.hadStomaAtSurg.rectal.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.hadStomaAtSurgColon}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.hadStomaAtSurgRectal}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Had abdominoperineal resection (rectal cancer only)</TableCell>
							<TableCell align="center">-</TableCell>
							<TableCell align="center">{`${crcData.level1.hadAbdominoperinealResection.rectal.percentage.toFixed(0)}%`} <br /> {`(${crcData.level1.hadAbdominoperinealResection.rectal.numerator}/${crcData.level1.hadAbdominoperinealResection.rectal.denominator})`}</TableCell>
							<TableCell align="center">-</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.hadAbdominoperinealResectionRectal}</TableCell>
						</TableRow>

						{/* <TableRow>
						<TableCell>Laparoscopic converted to open surgery (2014 only)</TableCell>
						<TableCell align = "center">{`${crcData.level1.lapConvertedOpenSurg.colon.percentage.toFixed(2)}%`} <br/> {`(${crcData.level1.lapConvertedOpenSurg.colon.numerator}/${crcData.level1.lapConvertedOpenSurg.colon.denominator})`}</TableCell>						
						<TableCell align = "center">{`${crcData.level1.lapConvertedOpenSurg.rectal.percentage.toFixed(2)}%`} <br/> {`(${crcData.level1.lapConvertedOpenSurg.rectal.numerator}/${crcData.level1.lapConvertedOpenSurg.rectal.denominator})`}</TableCell>
						<TableCell align = "center" style={{whiteSpace: "pre"}}>{stateWideData.lapConvertedToOpenSurgColon}</TableCell>
						<TableCell align = "center" style={{whiteSpace: "pre"}}>{stateWideData.lapConvertedToOpenSurgRectal}</TableCell>
						<TableCell align = "center">{(crcData.level1.lapConvertedOpenSurg.colon.denominator > 0 || crcData.level1.lapConvertedOpenSurg.rectal.denominator > 0) && downloadBtn(17)}</TableCell>
					</TableRow> */}

						<TableRow>
							<TableCell>In-Hospital mortality</TableCell>
							<TableCell align="center">{`${crcData.level1.inpatientDeath.colon.percentage.toFixed(1)}%`} <br /> {`(${crcData.level1.inpatientDeath.colon.numerator}/${crcData.level1.inpatientDeath.colon.denominator})`}</TableCell>
							<TableCell align="center">{`${crcData.level1.inpatientDeath.rectal.percentage.toFixed(2)}%`} <br /> {`(${crcData.level1.inpatientDeath.rectal.numerator}/${crcData.level1.inpatientDeath.rectal.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.inHospitalMortalityColon}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.inHospitalMortalityRectal}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell> 30 day mortality</TableCell>
							<TableCell align="center">{`${crcData.level1.death30Days.colon.percentage.toFixed(1)}%`} <br /> {`(${crcData.level1.death30Days.colon.numerator}/${crcData.level1.death30Days.colon.denominator})`}</TableCell>
							<TableCell align="center">{`${crcData.level1.death30Days.rectal.percentage.toFixed(2)}%`} <br /> {`(${crcData.level1.death30Days.rectal.numerator}/${crcData.level1.death30Days.rectal.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.mortality30DaysColon}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.mortality30DaysRectal}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell> 90 day mortality</TableCell>
							<TableCell align="center">{`${crcData.level1.death90Days.colon.percentage.toFixed(1)}%`} <br /> {`(${crcData.level1.death90Days.colon.numerator}/${crcData.level1.death90Days.colon.denominator})`}</TableCell>
							<TableCell align="center">{`${crcData.level1.death90Days.rectal.percentage.toFixed(1)}%`} <br /> {`(${crcData.level1.death90Days.rectal.numerator}/${crcData.level1.death90Days.rectal.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.mortality90DaysColon}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.mortality90DaysRectal}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>1 year surgical survival^</TableCell>
							<TableCell align="center">{`${crcData.level1.survival365Days.colon.percentage.toFixed(0)}%`} <br /> {`(${crcData.level1.survival365Days.colon.numerator}/${crcData.level1.survival365Days.colon.denominator})`}</TableCell>
							<TableCell align="center">{`${crcData.level1.survival365Days.rectal.percentage.toFixed(0)}%`} <br /> {`(${crcData.level1.survival365Days.rectal.numerator}/${crcData.level1.survival365Days.rectal.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.oneYearSurvivalColon}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.oneYearSurvivalRectal}</TableCell>
						</TableRow>
						<TableRow>
							<TableCell>2 year surgical survival^</TableCell>
							<TableCell align="center">{`${crcData.level1.survival730Days.colon.percentage.toFixed(0)}%`} <br /> {`(${crcData.level1.survival730Days.colon.numerator}/${crcData.level1.survival730Days.colon.denominator})`}</TableCell>
							<TableCell align="center">{`${crcData.level1.survival730Days.rectal.percentage.toFixed(0)}%`} <br /> {`(${crcData.level1.survival730Days.rectal.numerator}/${crcData.level1.survival730Days.rectal.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.twoYearSurvivalColon}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.twoYearSurvivalRectal}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell colSpan={6}> <i>^Patients with less than one and two years of data from their first surgery are excluded from the one and two year survival indicators respectively.</i></TableCell>
						</TableRow>

						{/* <TableRow>
						<TableCell> 5 year surgical survival</TableCell>
						<TableCell align = "center">{`${crcData.level1.survival1825Days.colon.percentage.toFixed(2)}%`} <br/> {`(${crcData.level1.survival1825Days.colon.numerator}/${crcData.level1.survival1825Days.colon.denominator})`}</TableCell>						
						<TableCell align = "center">{`${crcData.level1.survival1825Days.rectal.percentage.toFixed(2)}%`} <br/> {`(${crcData.level1.survival1825Days.rectal.numerator}/${crcData.level1.survival1825Days.rectal.denominator})`}</TableCell>
						<TableCell align = "center" style={{whiteSpace: "pre"}}>{stateWideData.fiveYearSurvivalColon}</TableCell>
						<TableCell align = "center" style={{whiteSpace: "pre"}}>{stateWideData.fiveYearSurvivalRectal}</TableCell>
					</TableRow> */}



						<TableRow>
							<TableCell colSpan={6}><b>Systemic Therapy</b></TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Received IV systemic therapy as first treatment within 42 days of diagnosis</TableCell>
							<TableCell align="center">{`${crcData.level1.diagToFirstST42Days.colon.percentage.toFixed(0)}%`} <br /> {`(${crcData.level1.diagToFirstST42Days.colon.numerator}/${crcData.level1.diagToFirstST42Days.colon.denominator})`}</TableCell>
							<TableCell align="center">{`${crcData.level1.diagToFirstST42Days.rectal.percentage.toFixed(0)}%`} <br /> {`(${crcData.level1.diagToFirstST42Days.rectal.numerator}/${crcData.level1.diagToFirstST42Days.rectal.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.hadFirstIVSTWT42DaysColon}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.hadFirstIVSTWT42DaysRectal}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Adjuvant IV systemic therapy for stage III patients</TableCell>
							<TableCell align="center">{`${crcData.level1.stage3Adjuvant.colon.percentage.toFixed(0)}%`} <br /> {`(${crcData.level1.stage3Adjuvant.colon.numerator}/${crcData.level1.stage3Adjuvant.colon.denominator})`}</TableCell>
							<TableCell align="center">{`${crcData.level1.stage3Adjuvant.rectal.percentage.toFixed(0)}%`} <br /> {`(${crcData.level1.stage3Adjuvant.rectal.numerator}/${crcData.level1.stage3Adjuvant.rectal.denominator})`}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.stage3AdjuvantSTColon}</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.stage3AdjuvantSTRectal}</TableCell>
						</TableRow>

						<TableRow>
							<TableCell colSpan={6}><b>Radiation Therapy</b></TableCell>
						</TableRow>

						<TableRow>
							<TableCell>Neoadjuvant radiation therapy rate (rectal cancer only)</TableCell>
							<TableCell align="center">-</TableCell>
							<TableCell align="center">{`${crcData.level1.neoAdjuvantXRT.rectal.percentage.toFixed(0)}%`} <br /> {`(${crcData.level1.neoAdjuvantXRT.rectal.numerator}/${crcData.level1.neoAdjuvantXRT.rectal.denominator})`}</TableCell>
							<TableCell align="center">-</TableCell>
							<TableCell align="center" style={{ whiteSpace: "pre" }}>{stateWideData.neoadjuvantXRTRectal}</TableCell>
						</TableRow>

					</TableBody>
				</Table>
			</TableContainer>

			<div style={{ fontSize: "small", textAlign: "left" }}>
				<p><i>*Data based on facility of MDT</i> </p>
			</div>

			<div style={{ fontSize: "small", textAlign: "left" }}>
				<p><i>^The comparison data in the Qld report is based on a person's incidence case of the cancer. The MDT data is all diagnoses (with a diagnosis date) presented to MDT using the Qool application</i> </p>
			</div>

			<div style={{ textAlign: "left" }}>
				<p>This report is based on the <a href={ColonIndicatorDefinitionsUrl} target="_blank">Queensland Colon Cancer Quality Index 2012-2021 </a>
					and <a href={RectalIndicatorDefinitionsUrl} target="_blank">Queensland Rectal Cancer Quality Index 2012-2021.</a> Indicator definitions and glossary can be found in the technical appendix</p>
			</div>
		</div>

		<a id="exportPatientsElem" style={{ display: "none" }}></a>

	</>

	return (
		dashboard
	)

}